import { fetchClient } from "@/api/api";
import type {
	addBonusInput,
	addBonusOutput,
	deleteBonusInput,
	getAllBonusInput,
	getAllBonusOutput,
	getBonusInput,
	getBonusOutput,
	updateBonusInput,
	updateBonusOutput,
} from "@/api/custom_types";
import useApiError from "@/hooks/useApiError";
import type { ApiError } from "@/utils/fetching";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { invalidateQueries } from "@/utils/react-query";
import { toast } from "@st-marketplace/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { BONUS_SERVICE_KEYS } from "./keys";

export const useGetAllBonus: CustomQueryHook<
	getAllBonusOutput,
	getAllBonusInput
> = (options) => {
	return useQuery<getAllBonusOutput, ApiError>({
		...options,
		queryKey: BONUS_SERVICE_KEYS.useGetAllBonus(),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/fc/bonus/", {
				throwOnError: true,
			});
			if (error) throw error;
			return data;
		},
	});
};

export const useAddBonus: CustomMutationHook<addBonusInput, addBonusOutput> =
	() => {
		const { toastApiError } = useApiError();

		return useMutation<addBonusOutput, ApiError, addBonusInput>({
			mutationKey: BONUS_SERVICE_KEYS.useAddBonus(),
			mutationFn: async (body) => {
				const { data, error } = await fetchClient.POST("/v1/fc/bonus/", {
					body,
					throwOnError: true,
				});
				if (error) {
					throw error;
				}
				return data;
			},
			onSuccess: () => {
				invalidateQueries(BONUS_SERVICE_KEYS.useGetAllBonus());
				toast.success("Bonus added successfully");
			},
			onError: (error) => {
				toastApiError(error);
			},
		});
	};

export const useGetBonus: CustomQueryHook<getBonusOutput, getBonusInput> = (
	params,
	options,
) => {
	return useQuery<getBonusOutput, ApiError>({
		...options,
		queryKey: BONUS_SERVICE_KEYS.useGetBonus(params),
		enabled: !!params.bonus_rule_id,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/bonus/{bonus_rule_id}",
				{
					params: {
						path: { bonus_rule_id: params.bonus_rule_id },
					},
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
	});
};

export const useUpdateBonus: CustomMutationHook<
	updateBonusInput,
	updateBonusOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<updateBonusOutput, ApiError, updateBonusInput>({
		mutationKey: BONUS_SERVICE_KEYS.useUpdateBonus(),
		mutationFn: async (payload) => {
			const { ...body } = payload;
			const { data, error } = await fetchClient.PATCH(
				"/v1/fc/bonus/{bonus_rule_id}",
				{
					params: {
						path: { bonus_rule_id: "" },
					},
					body,
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: () => {
			invalidateQueries(BONUS_SERVICE_KEYS.useGetAllBonus());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useDeleteBonus: CustomMutationHook<deleteBonusInput, any> = () => {
	const { toastApiError } = useApiError();

	return useMutation<any, ApiError, deleteBonusInput>({
		mutationKey: BONUS_SERVICE_KEYS.useDeleteBonus(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.DELETE(
				"/v1/fc/bonus/{bonus_rule_id}",
				{
					params: {
						path: { bonus_rule_id: payload.bonus_rule_id },
					},
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: () => {
			invalidateQueries(BONUS_SERVICE_KEYS.useGetAllBonus());
			toast.success("Bonus deleted successfully");
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};
